// Globals
import { TFunction } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';

// Models
import { IStatusHistoryItem, OrdersStatus } from '@/models/orders.model';

// Helpers
import { getStatusStepState, StatusStepState } from '../../helpers/getStatusStepState';
import { assertUnreachable } from '@/utils';

// Components
import {
  StyledWrapper,
  StyledTitle,
  StyledTimeline,
  StyledTimelineItem,
  StyledIconWrapper,
  StyledContent,
  StyledStatusName,
  StyledCurrentBadge,
  StyledDate,
  StyledQuestionIcon,
} from './StatusHistoryBlockStyles';
import { Tooltip } from '@/ui';
import { CheckmarkIcon, CrossIcon, MinusIcon, StatusClockIcon } from '@/icons';

interface StatusHistoryBlockProps {
  t: TFunction;
  statusHistory: IStatusHistoryItem[];
  currentStatus?: OrdersStatus;
}

/**
 * Mapping of order statuses to their translation keys.
 * Used for displaying localized status names and tooltips in the timeline.
 */
const STATUS_LABELS: Record<OrdersStatus, { labelKey: string; tooltipKey: string }> = {
  [OrdersStatus.NEW]: {
    labelKey: 'status_history_new',
    tooltipKey: 'status_history_new_tooltip',
  },
  [OrdersStatus.PROCESSING]: {
    labelKey: 'status_history_processing',
    tooltipKey: 'status_history_processing_tooltip',
  },
  [OrdersStatus.ACCEPTED]: {
    labelKey: 'status_history_approved',
    tooltipKey: 'status_history_approved_tooltip',
  },
  [OrdersStatus.READY_FOR_TTN]: {
    labelKey: 'status_history_ready_for_ttn',
    tooltipKey: 'status_history_ready_for_ttn_tooltip',
  },
  [OrdersStatus.TRANSFERRED]: {
    labelKey: 'status_history_transferred',
    tooltipKey: 'status_history_transferred_tooltip',
  },
  [OrdersStatus.AWAITING]: {
    labelKey: 'status_history_awaiting',
    tooltipKey: 'status_history_awaiting_tooltip',
  },
  [OrdersStatus.RESERVE]: {
    labelKey: 'status_history_reserve',
    tooltipKey: 'status_history_reserve_tooltip',
  },
  [OrdersStatus.COMPLETED]: {
    labelKey: 'status_history_completed',
    tooltipKey: 'status_history_completed_tooltip',
  },
  [OrdersStatus.CANCELLED]: {
    labelKey: 'status_history_cancelled',
    tooltipKey: 'status_history_cancelled_tooltip',
  },
};

/**
 * UI display order for status timeline.
 * Keep this independent from API enum order to preserve business flow in the interface.
 */
const STATUS_DISPLAY_ORDER: OrdersStatus[] = [
  OrdersStatus.NEW,
  OrdersStatus.RESERVE,
  OrdersStatus.PROCESSING,
  OrdersStatus.ACCEPTED,
  OrdersStatus.READY_FOR_TTN,
  OrdersStatus.TRANSFERRED,
  OrdersStatus.AWAITING,
  OrdersStatus.COMPLETED,
  OrdersStatus.CANCELLED,
];

/**
 * Formats a date string for display in Ukrainian locale.
 * Returns '—' for invalid or missing dates.
 */
const formatStatusDate = (date: string | null): string => {
  if (!date) return '—';

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) return '—';

  return parsedDate.locale('uk').format('D MMMM, HH:mm:ss');
};

const renderStatusIcon = (state: StatusStepState) => {
  switch (state) {
    case 'completed':
      return <CheckmarkIcon />;
    case 'current':
      return <StatusClockIcon />;
    case 'skipped':
      return <MinusIcon />;
    case 'cancelled':
      return <CrossIcon />;
    case 'future':
      return null;
    default:
      return assertUnreachable(state);
  }
};

const StatusHistoryBlock = ({ t, statusHistory, currentStatus }: StatusHistoryBlockProps) => {
  if (!statusHistory || !Array.isArray(statusHistory) || statusHistory.length === 0) {
    return null;
  }

  const historyByStatus = statusHistory.reduce((acc, item) => {
    acc[item.status] = item;
    return acc;
  }, {} as Partial<Record<OrdersStatus, IStatusHistoryItem>>);

  const orderedStatusHistory = STATUS_DISPLAY_ORDER.map((status) => historyByStatus[status]).filter(
    (item): item is IStatusHistoryItem => !!item,
  );

  return (
    <StyledWrapper>
      <StyledTitle>{t('status_history_title')}</StyledTitle>
      <StyledTimeline>
        {orderedStatusHistory.map((item) => {
          const config = STATUS_LABELS[item.status];
          if (!config) return null;

          const { labelKey, tooltipKey } = config;
          const state = getStatusStepState(item, orderedStatusHistory, currentStatus);
          const hasValidDate = !!item.date && dayjs(item.date).isValid();
          const isMuted = state === 'future' || state === 'skipped';

          return (
            <StyledTimelineItem key={item.status}>
              <StyledIconWrapper className={`is-${state}`}>{renderStatusIcon(state)}</StyledIconWrapper>
              <StyledContent>
                <StyledStatusName $muted={isMuted}>
                  {t(labelKey)}
                  <Tooltip title={t(tooltipKey)}>
                    <StyledQuestionIcon />
                  </Tooltip>
                </StyledStatusName>
                {state === 'current' && <StyledCurrentBadge>{t('status_history_current')}</StyledCurrentBadge>}
                <StyledDate>{hasValidDate ? formatStatusDate(item.date) : '—'}</StyledDate>
              </StyledContent>
            </StyledTimelineItem>
          );
        })}
      </StyledTimeline>
    </StyledWrapper>
  );
};

export default StatusHistoryBlock;
